/**
 * MainLayoutContext
 *
 * Provides a Layout-level API for pages to request changes to the <main>
 * element's overflow style. This avoids the anti-pattern of reaching outside
 * a component's own tree via document.getElementById.
 *
 * Usage:
 *   const { setMainOverflowX } = useMainLayout();
 *   useEffect(() => {
 *     setMainOverflowX('auto');
 *     return () => setMainOverflowX('');
 *   }, [setMainOverflowX]);
 */
import React, { createContext, useCallback, useContext, useState } from 'react';

interface MainLayoutContextValue {
  mainOverflowX: string;
  setMainOverflowX: (value: string) => void;
}

const MainLayoutContext = createContext<MainLayoutContextValue | null>(null);

export const MainLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mainOverflowX, setMainOverflowXState] = useState('');

  const setMainOverflowX = useCallback((value: string) => {
    setMainOverflowXState(value);
  }, []);

  return (
    <MainLayoutContext.Provider value={{ mainOverflowX, setMainOverflowX }}>
      {children}
    </MainLayoutContext.Provider>
  );
};

export const useMainLayout = (): MainLayoutContextValue => {
  const ctx = useContext(MainLayoutContext);
  if (!ctx) {
    throw new Error('useMainLayout must be used within a MainLayoutProvider');
  }
  return ctx;
};
